import React from 'react';
import { Disc, Activity, Sparkles } from 'lucide-react';
import type { BrakingZoneData, BrakingLapEntry, BrakingStats } from '@/types/telemetry';
import { useI18n } from '@/context/I18nContext';
import { formatTime } from '@/lib/format';
import { formatDynamicCornerLabel } from '@/lib/spline';

interface BrakingStatsCardProps {
  zone: BrakingZoneData;
  activeLap?: BrakingLapEntry | null;
}

export function BrakingStatsCard({ zone, activeLap }: BrakingStatsCardProps) {
  const { t } = useI18n();
  const laps = zone.laps || [];

  const decelStats = zone.decelStats;
  const initialSpeedStats = zone.initialSpeedStats;
  const speedDropStats = zone.speedDropStats;
  const lateralGStats = zone.lateralGStats;

  // Trail-Braking Score from backend
  const trailScoreMedian = zone.trailScoreStats?.median ?? 70;
  const activeTrailScore = activeLap?.trailBrakingScore;
  const currentTrailScore = activeTrailScore ?? trailScoreMedian;

  const scoreTier =
    currentTrailScore >= 80 ? 'optimal' : currentTrailScore >= 65 ? 'good' : 'needsWork';

  const tierBadgeClass =
    scoreTier === 'optimal'
      ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
      : scoreTier === 'good'
      ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
      : 'bg-red-500/15 text-red-400 border-red-500/30';

  const tierText =
    scoreTier === 'optimal'
      ? t('braking.scoreOptimal')
      : scoreTier === 'good'
      ? t('braking.scoreGood')
      : t('braking.scoreNeedsWork');

  const adviceText =
    scoreTier === 'optimal'
      ? t('braking.trailScoreAdviceOptimal')
      : scoreTier === 'good'
      ? t('braking.trailScoreAdviceGood')
      : t('braking.trailScoreAdviceNeedsWork');

  // Turn title resolution
  const turnNumber = zone.orderIndex > 0 ? zone.orderIndex : 1;
  const zoneTitle = formatDynamicCornerLabel(
    zone.name,
    turnNumber,
    t('corner.turn') || 'Turn'
  );

  const renderQuartileBar = (
    label: string,
    stats?: BrakingStats,
    currentValue?: number,
    unit = '',
    formatFn = (v: number) => v.toFixed(1)
  ) => {
    const min = stats?.min ?? 0;
    const max = stats?.max ?? 0;
    const median = stats?.median ?? 0;
    const q1 = stats?.q1 ?? (median > min ? min + (median - min) * 0.5 : min);
    const q3 = stats?.q3 ?? (max > median ? median + (max - median) * 0.5 : max);

    const range = max - min || 1;
    const q1Pct = Math.max(0, Math.min(100, ((q1 - min) / range) * 100));
    const medPct = Math.max(0, Math.min(100, ((median - min) / range) * 100));
    const q3Pct = Math.max(0, Math.min(100, ((q3 - min) / range) * 100));
    const currPct =
      currentValue != null ? Math.max(0, Math.min(100, ((currentValue - min) / range) * 100)) : null;

    return (
      <div className="bg-bg-surface border border-border-subtle hover:border-border-default/80 transition-colors rounded-xl p-4 flex flex-col justify-between shadow-xs min-w-0">
        <div>
          <div className="flex items-start justify-between gap-2 mb-2 min-w-0">
            <span className="text-xs font-semibold text-text-secondary truncate" title={label}>
              {label}
            </span>
            {currentValue != null && (
              <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-md bg-accent/15 border border-accent/25 text-accent font-mono font-medium whitespace-nowrap">
                {t('braking.lap')} {activeLap?.lapNumber ? `#${activeLap.lapNumber}: ` : ''}{formatFn(currentValue)} {unit}
              </span>
            )}
          </div>
          <div className="flex items-baseline justify-between">
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold font-mono text-text-primary tracking-tight">
                {formatFn(median)}
              </span>
              <span className="text-xs font-medium text-text-muted">{unit}</span>
            </div>
            <span className="text-[10px] uppercase font-semibold text-text-muted/60 tracking-wider">
              {t('sectorPanel.median') || 'Median'}
            </span>
          </div>
        </div>

        {/* Quartile distribution bar */}
        <div className="mt-4 pt-3 border-t border-border-subtle/80">
          <div className="relative w-full h-2 bg-bg-base rounded-full my-2 overflow-visible">
            {/* IQR Box (Q1 to Q3) */}
            <div
              className="absolute top-0 bottom-0 bg-accent/25 border-x border-accent/40 rounded-xs"
              style={{ left: `${q1Pct}%`, width: `${Math.max(3, q3Pct - q1Pct)}%` }}
            />
            {/* Median line */}
            <div
              className="absolute -top-0.5 -bottom-0.5 w-1 bg-accent rounded-full -ml-0.5 shadow-xs"
              style={{ left: `${medPct}%` }}
            />
            {/* Active Lap marker */}
            {currPct != null && (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 -ml-1.5 bg-white border-2 border-accent rounded-full shadow-md z-10"
                style={{ left: `${currPct}%` }}
                title={`${t('braking.selectedLap')}: ${formatFn(currentValue!)} ${unit}`}
              />
            )}
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono text-text-muted px-0.5">
            <span className="whitespace-nowrap">
              <span className="text-text-muted/50 text-[9px] uppercase font-sans">{t('filter.min')} </span>
              {formatFn(min)}
            </span>
            <span className="whitespace-nowrap text-text-secondary font-semibold">
              <span className="text-accent/70 text-[9px] uppercase font-sans">{t('sectorPanel.median')} </span>
              {formatFn(median)}
            </span>
            <span className="whitespace-nowrap">
              <span className="text-text-muted/50 text-[9px] uppercase font-sans">{t('filter.max')} </span>
              {formatFn(max)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="card p-5 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 pb-4 border-b border-border-subtle">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent shadow-xs">
            <Disc size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
              <span>{zoneTitle}</span>
              <span className="text-xs font-normal text-text-muted px-2 py-0.5 rounded-full bg-bg-surface border border-border-subtle">
                {laps.length} {t('braking.allLaps')}
              </span>
            </h2>
            <p className="text-xs text-text-muted">
              {t('braking.desc')}
            </p>
          </div>
        </div>

        {/* Selected Lap Indicator */}
        {activeLap && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-bg-surface border border-border-subtle text-xs">
            <span className="text-text-muted">{t('braking.selectedLap')}:</span>
            <span className="font-semibold text-text-primary">
              {t('braking.lap')} #{activeLap.lapNumber}
            </span>
            <span className="text-text-muted">·</span>
            <span className="text-text-secondary">{activeLap.driverName}</span>
            {activeLap.lapTimeMs > 0 && (
              <>
                <span className="text-text-muted">·</span>
                <span className="font-mono text-accent font-medium">
                  {formatTime(activeLap.lapTimeMs)}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Trail-Braking Score Hero Block */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-accent/10 via-bg-surface to-bg-surface border border-accent/25 p-5 shadow-sm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-center">
          {/* Left: Score & Tier */}
          <div className="lg:col-span-4 flex items-center gap-4 border-b lg:border-b-0 lg:border-r border-border-subtle/80 pb-4 lg:pb-0 lg:pr-5">
            <div className="w-14 h-14 rounded-2xl bg-accent/20 border border-accent/30 flex items-center justify-center text-accent shrink-0 shadow-inner">
              <Activity size={28} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs uppercase font-bold tracking-wider text-text-secondary">
                  {t('braking.trailScore')}
                </span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${tierBadgeClass}`}>
                  {tierText}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-extrabold font-mono text-text-primary tracking-tight">
                  {currentTrailScore.toFixed(0)}%
                </span>
                {activeLap && activeTrailScore != null && (
                  <span className="text-xs text-text-muted font-mono">
                    ({t('braking.sessionMedian')}: {trailScoreMedian.toFixed(0)}%)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Middle: Coaching Advice */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-accent mb-1">
              <Sparkles size={14} />
              <span>{t('braking.trailScoreDesc')}</span>
            </div>
            <p className="text-xs text-text-secondary leading-relaxed">
              {adviceText}
            </p>
          </div>

          {/* Right: Distribution Across Session */}
          <div className="lg:col-span-3 flex flex-col justify-center bg-bg-base/60 border border-border-subtle/60 rounded-xl p-3">
            <div className="flex items-center justify-between text-[11px] mb-1 text-text-muted">
              <span className="font-semibold uppercase text-[10px] tracking-wider text-text-secondary">
                {t('sectorPanel.consistency') || 'Consistency'}
              </span>
              <span className="font-mono text-text-primary font-medium">
                {zone.trailScoreStats?.min?.toFixed(0) ?? '—'}% - {zone.trailScoreStats?.max?.toFixed(0) ?? '—'}%
              </span>
            </div>
            {/* Trail Score Bar */}
            <div className="relative w-full h-2 bg-bg-surface rounded-full my-1.5 overflow-hidden">
              <div
                className="absolute top-0 bottom-0 bg-accent/40 rounded-full"
                style={{
                  left: `${Math.max(0, Math.min(100, zone.trailScoreStats?.min ?? 0))}%`,
                  width: `${Math.max(4, (zone.trailScoreStats?.max ?? 100) - (zone.trailScoreStats?.min ?? 0))}%`,
                }}
              />
              <div
                className="absolute top-0 bottom-0 w-1 bg-accent"
                style={{
                  left: `${Math.max(0, Math.min(100, currentTrailScore))}%`,
                }}
              />
            </div>
            <div className="flex items-center justify-between text-[9px] text-text-muted font-mono">
              <span>{t('filter.min')}</span>
              <span className="text-text-secondary font-medium">{t('sectorPanel.median')} {trailScoreMedian.toFixed(0)}%</span>
              <span>{t('filter.max')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Primary KPI Grid (Deceleration & Dynamics) */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-text-muted">
            {t('braking.decelAndGrip')}
          </span>
          <div className="h-px bg-border-subtle flex-1" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
          {renderQuartileBar(
            t('braking.distance'),
            zone.distanceStats,
            activeLap?.brakingDistanceM,
            'm',
            v => v.toFixed(1)
          )}
          {renderQuartileBar(
            t('braking.duration'),
            zone.durationStats,
            activeLap?.brakingDurationMs,
            's',
            v => (v / 1000).toFixed(2)
          )}
          {renderQuartileBar(
            t('braking.peakDecel'),
            decelStats,
            activeLap ? Math.abs(activeLap.peakLongitudinalG) : undefined,
            'G',
            v => Math.abs(v).toFixed(2)
          )}
          {renderQuartileBar(
            t('braking.peakLateral'),
            lateralGStats,
            activeLap?.peakLateralG,
            'G',
            v => v.toFixed(2)
          )}
        </div>
      </div>

      {/* Speed Transition Grid */}
      <div className="space-y-3 pt-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-text-muted">
            {t('braking.speedTransition')}
          </span>
          <div className="h-px bg-border-subtle flex-1" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          {renderQuartileBar(
            t('braking.initialSpeed'),
            initialSpeedStats,
            activeLap?.initialSpeedKmh,
            'km/h'
          )}
          {renderQuartileBar(
            t('braking.minSpeed'),
            zone.minSpeedStats ?? zone.apexSpeedStats,
            activeLap?.minSpeedKmh ?? activeLap?.apexSpeedKmh,
            'km/h'
          )}
          {renderQuartileBar(
            t('braking.speedDrop'),
            speedDropStats,
            activeLap?.speedDropKmh,
            'km/h'
          )}
        </div>
      </div>
    </div>
  );
}
